import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import { PiMapPin, PiMagnifyingGlass } from 'react-icons/pi';
import TextInput from '../../atoms/inputs/text-input';
import { Text } from '../../atoms/text';
import { EmptyState } from '../../atoms/states/empty-state';
import { useI18n } from '../../../hooks/provider-bounded/i18n';
import {
  getMenuItemClasses,
  menuListClasses,
} from '../../../styles/dropdown.styles';
import type { LocationAutocompleteSuggestion } from '../../../types/location/geocoding.type';
import {
  resolveGoogleMapsLocationPrediction,
  searchGoogleMapsLocationPredictions,
  type GoogleMapsPrediction,
} from '../../../utils/location/google-maps-geocoding.service';

interface LocationAutocompleteInputProps {
  /** Invalidates pending place details when the map or saved country changes. */
  selectionResetKey?: number;
  /** Notify the owner before resolving a newly selected address. */
  onSelectionStart?: () => void;
  /** Report pending place details separately from prediction searches. */
  onSelectionPendingChange?: (pending: boolean) => void;
  value?: string | null;
  /** Explicit optional country restriction. */
  countryCode?: string | null;
  /** Regional ranking preference for worldwide search. */
  regionCode?: string | null;
  disabled?: boolean;
  placeholder?: string;
  onSelect: (suggestion: LocationAutocompleteSuggestion) => void;
}

const DEBOUNCE_MS = 350;

/**
 * Shared address autocomplete input backed by the geocoding provider used by
 * the property editor.
 */
export const LocationAutocompleteInput: React.FC<
  LocationAutocompleteInputProps
> = ({
  value,
  selectionResetKey,
  countryCode,
  regionCode,
  disabled = false,
  placeholder,
  onSelect,
  onSelectionStart,
  onSelectionPendingChange,
}) => {
  const { t } = useI18n();
  const selectionRevision = useRef(0);
  const [selectionPending, setSelectionPending] = useState(false);
  useEffect(() => {
    onSelectionPendingChange?.(selectionPending);
  }, [onSelectionPendingChange, selectionPending]);
  const [selectionError, setSelectionError] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [query, setQuery] = useState(value ?? '');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [predictions, setPredictions] = useState<GoogleMapsPrediction[]>([]);
  const [configurationError, setConfigurationError] = useState(false);
  const debouncedQuery = useDebounce(query, DEBOUNCE_MS);
  const trimmedQuery = query.trim();
  const trimmedDebouncedQuery = debouncedQuery.trim();

  useEffect(() => {
    selectionRevision.current += 1;
    setSelectionPending(false);
    setQuery(value ?? '');
  }, [value]);

  useEffect(() => {
    selectionRevision.current += 1;
    setSelectionPending(false);
    return () => {
      selectionRevision.current += 1;
    };
  }, [countryCode, disabled, selectionResetKey]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (disabled) {
      setPredictions([]);
      setLoading(false);
      return;
    }

    if (trimmedDebouncedQuery.length < 3) {
      setPredictions([]);
      setConfigurationError(false);
      setSearchError(false);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);

    void searchGoogleMapsLocationPredictions({
      query: trimmedDebouncedQuery,
      countryCode,
      regionCode,
    })
      .then((nextPredictions) => {
        if (!active) return;
        setPredictions(nextPredictions);
        setConfigurationError(false);
        setSearchError(false);
      })
      .catch((error: unknown) => {
        if (!active) return;
        setPredictions([]);
        setSearchError(true);
        setConfigurationError(
          error instanceof Error &&
            error.message === 'Google Maps API key is not configured.'
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [countryCode, regionCode, disabled, trimmedDebouncedQuery]);

  const hasResults = predictions.length > 0;
  const isWaitingForDebounce =
    trimmedQuery.length >= 3 && trimmedQuery !== trimmedDebouncedQuery;
  const showDropdown =
    open &&
    (loading ||
      isWaitingForDebounce ||
      hasResults ||
      configurationError ||
      trimmedDebouncedQuery.length >= 3);
  const helperText = useMemo(() => {
    if (selectionError) return t('shared.location.autocomplete.resolveError');
    if (configurationError) {
      return t('shared.location.autocomplete.configurationMissing');
    }

    if (searchError) return t('shared.location.autocomplete.searchError');
    return t('shared.location.autocomplete.help');
  }, [configurationError, selectionError, searchError, t]);

  const handleSelectPrediction = async (prediction: GoogleMapsPrediction) => {
    const revision = ++selectionRevision.current;
    onSelectionStart?.();
    setSelectionPending(true);
    setSelectionError(false);
    setQuery(prediction.description);
    setOpen(false);
    setLoading(true);

    try {
      const suggestion = await resolveGoogleMapsLocationPrediction(prediction);
      if (revision !== selectionRevision.current) return;
      if (suggestion) {
        onSelect(suggestion);
      } else {
        setSelectionError(true);
      }
    } catch {
      if (revision === selectionRevision.current) setSelectionError(true);
    } finally {
      if (revision === selectionRevision.current) {
        setLoading(false);
        setSelectionPending(false);
      }
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <div className="relative">
        <TextInput
          value={query}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            selectionRevision.current += 1;
            setSelectionPending(false);
            setSelectionError(false);
            setQuery(event.target.value);
            setOpen(true);
          }}
          className="pr-10"
        />
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-slate-400">
          <PiMagnifyingGlass className="h-4 w-4" />
        </div>
      </div>

      {showDropdown ? (
        <div className="absolute left-0 right-0 top-[calc(100%+0.5rem)] z-[10000] overflow-hidden rounded-2xl border border-slate-200 bg-white p-0 shadow-xl">
          <div
            className={`${menuListClasses} overscroll-contain scroll-smooth py-2`}
          >
            {loading || isWaitingForDebounce ? (
              <div className="px-3 py-4">
                <Text size="small" className="text-slate-500">
                  {t('shared.location.autocomplete.loading')}
                </Text>
              </div>
            ) : hasResults ? (
              predictions.map((prediction) => (
                <button
                  key={prediction.placeId}
                  type="button"
                  className={`${getMenuItemClasses('md')} items-start gap-3 rounded-xl px-3 py-3 text-left`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => void handleSelectPrediction(prediction)}
                >
                  <div className="mt-0.5 rounded-full bg-slate-100 p-2 text-slate-500">
                    <PiMapPin className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <Text
                      size="small"
                      weight="medium"
                      className="truncate text-slate-900"
                    >
                      {prediction.description}
                    </Text>
                    {prediction.secondaryText ? (
                      <Text size="tiny" className="mt-0.5 text-slate-500">
                        {prediction.secondaryText}
                      </Text>
                    ) : null}
                  </div>
                </button>
              ))
            ) : (
              <EmptyState
                title={t(
                  searchError
                    ? 'shared.location.autocomplete.searchError'
                    : 'shared.location.autocomplete.empty'
                )}
                description={t('shared.location.autocomplete.help')}
                icon={<PiMapPin className="h-4 w-4" />}
                size="xs"
                className="py-6"
              />
            )}
          </div>
        </div>
      ) : null}

      <Text size="tiny" className="mt-1 text-slate-500">
        {helperText}
      </Text>
    </div>
  );
};
